<template>
  <div class="extensions-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader pageTitle="Extensions" />
      <main class="page-content">

        <!-- Page Header -->
        <div class="page-header">
          <div class="header-content">
            <h1>Extensions</h1>
            <p class="subtitle">Agent ↔ FreePBX extension assignments.</p>
          </div>
          <div class="header-right">
            <div class="online-count" v-if="onlineCount > 0">
              <span class="online-dot"></span>
              {{ onlineCount }} online
            </div>
            <button class="btn-secondary" @click="load" :disabled="loading">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>
              Refresh
            </button>
          </div>
        </div>

        <!-- Search -->
        <div class="search-bar" v-if="!loading && !error">
          <div class="search-wrap">
            <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input v-model="searchQuery" class="search-input" placeholder="Search by agent name, code, or extension..." />
            <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
            </button>
          </div>
          <div class="result-count" v-if="searchQuery">{{ filteredAgents.length }} result{{ filteredAgents.length !== 1 ? 's' : '' }}</div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="error-state">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
          <div>
            <div>{{ error }}</div>
            <button class="retry-btn" @click="load">Retry</button>
          </div>
        </div>

        <!-- Table -->
        <div v-else class="table-card">
          <table class="data-table" v-if="filteredAgents.length">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Extension</th>
                <th>Type</th>
                <th>Status</th>
                <th>Last Registered</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="agent in filteredAgents" :key="agent.agent_id" :class="{ 'row-connected': agent.connected }">
                <!-- Agent -->
                <td>
                  <div class="agent-cell">
                    <div class="agent-avatar" :class="{ 'avatar-connected': agent.connected }">
                      {{ agent.name.charAt(0).toUpperCase() }}
                    </div>
                    <div>
                      <div class="agent-name">{{ agent.name }}</div>
                      <div class="agent-code-badge">{{ agent.agent_code }}</div>
                    </div>
                  </div>
                </td>
                <!-- Extension -->
                <td>
                  <div v-if="agent.extension" class="ext-cell">
                    <span class="ext-number">{{ agent.extension }}</span>
                    <span class="connected-badge">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                      connected
                    </span>
                  </div>
                  <span v-else class="no-ext">Not connected</span>
                </td>
                <!-- Type -->
                <td>
                  <span v-if="agent.type" class="type-badge">{{ agent.type.toUpperCase() }}</span>
                  <span v-else class="muted-cell">—</span>
                </td>
                <!-- Status -->
                <td>
                  <div v-if="agent.status" class="status-cell">
                    <span class="status-dot" :class="'dot-' + agent.status"></span>
                    <span class="status-label" :class="'label-' + agent.status">{{ statusLabel(agent.status) }}</span>
                  </div>
                  <span v-else class="muted-cell">—</span>
                </td>
                <!-- Last Registered -->
                <td class="muted-cell">{{ agent.last_registered_at ? formatDate(agent.last_registered_at) : '—' }}</td>
                <!-- Actions -->
                <td class="action-cell">
                  <button v-if="agent.connected" class="btn-action btn-edit" @click="openEdit(agent)">
                    Edit
                  </button>
                  <button v-else class="btn-action btn-connect" @click="openConnect(agent)">
                    Connect
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-else-if="searchQuery" class="empty-state">
            <div class="empty-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            </div>
            <h3>No results for "{{ searchQuery }}"</h3>
            <p>Try searching by a different name, code, or extension number.</p>
          </div>

          <div v-else class="empty-state">
            <div class="empty-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>
            </div>
            <h3>No agents found</h3>
            <p>Make sure the FreePBX module is connected and syncing.</p>
          </div>
        </div>

      </main>
    </div>

    <!-- Connect / Edit Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ modalMode === 'edit' ? 'Edit Extension Connection' : 'Connect Agent to Extension' }}</h2>
          <button class="modal-close" @click="closeModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </div>

        <div class="modal-body">

          <!-- Agent display -->
          <div class="agent-display">
            <div class="agent-display-avatar">{{ modalAgent.name.charAt(0).toUpperCase() }}</div>
            <div>
              <div class="agent-display-name">{{ modalAgent.name }}</div>
              <div class="agent-display-code">{{ modalAgent.agent_code }}</div>
            </div>
          </div>

          <!-- Save error -->
          <div v-if="saveError" class="save-error">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
            {{ saveError }}
          </div>

          <!-- Extension picker -->
          <div class="form-group">
            <label class="form-label">Extension #</label>
            <div class="ext-picker">
              <!-- Filter inside picker -->
              <div class="ext-picker-search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input
                  v-model="extSearchQuery"
                  class="ext-picker-input"
                  placeholder="Filter extensions..."
                />
              </div>
              <div class="ext-picker-list">
                <div v-if="filteredExtensions.length === 0" class="ext-picker-empty">No extensions found</div>
                <button
                  v-for="ext in filteredExtensions"
                  :key="ext.extension"
                  type="button"
                  class="ext-picker-item"
                  :class="{
                    'ext-item-selected': form.extension === ext.extension,
                    'ext-item-disabled': usedExtensions[ext.extension] && usedExtensions[ext.extension].agent_id !== modalAgent.agent_id
                  }"
                  :disabled="!!(usedExtensions[ext.extension] && usedExtensions[ext.extension].agent_id !== modalAgent.agent_id)"
                  @click="form.extension = ext.extension"
                >
                  <div class="ext-item-left">
                    <span class="ext-item-number">{{ ext.extension }}</span>
                    <span class="ext-item-name">{{ ext.display_name || 'Unknown' }}</span>
                  </div>
                  <div class="ext-item-right">
                    <span v-if="usedExtensions[ext.extension] && usedExtensions[ext.extension].agent_id !== modalAgent.agent_id" class="ext-item-inuse">
                      In use by {{ usedExtensions[ext.extension].name }}
                    </span>
                    <template v-else>
                      <span v-if="ext.type" class="ext-item-type">{{ ext.type }}</span>
                      <span v-if="ext.status" class="ext-item-status" :class="'ext-status-' + ext.status.toLowerCase()"></span>
                    </template>
                    <svg v-if="form.extension === ext.extension" width="14" height="14" viewBox="0 0 24 24" fill="#3B82F6"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <!-- SIP Password -->
          <div class="form-group">
            <label class="form-label">
              SIP Password
              <span class="label-optional">optional</span>
            </label>
            <div class="password-wrap">
              <input
                v-model="form.sip_password"
                :type="showPassword ? 'text' : 'password'"
                class="form-input"
                :placeholder="modalMode === 'edit' ? '(saved — leave blank to keep)' : 'Leave blank if not required'"
              />
              <button class="show-pwd-btn" type="button" @click="showPassword = !showPassword">
                <svg v-if="!showPassword" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
                <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/></svg>
              </button>
            </div>
          </div>

          <!-- Connection summary -->
          <div v-if="form.extension" class="connect-summary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>
            <span>Connecting <strong>{{ modalAgent.name }}</strong> to Extension <strong>{{ form.extension }}</strong></span>
          </div>

        </div>

        <div class="modal-footer">
          <button class="btn-cancel" @click="closeModal" :disabled="saving">Cancel</button>
          <button class="btn-save" @click="saveConnection" :disabled="saving || !form.extension">
            <span v-if="saving" class="btn-spinner"></span>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>
            {{ saving ? 'Connecting...' : 'Connect Agent To Extension' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { IVRService } from '@/services/ivrService'

export default {
  name: 'ExtensionsView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      agents: [],
      pbxExtensions: [],
      sipHost: null,
      sipPort: 5060,
      loading: true,
      error: null,
      searchQuery: '',
      // Modal
      showModal: false,
      modalMode: 'connect',
      modalAgent: null,
      form: {
        extension: '',
        sip_password: ''
      },
      showPassword: false,
      saving: false,
      saveError: null,
      extSearchQuery: ''
    }
  },

  computed: {
    filteredAgents () {
      const sorted = [...this.agents].sort((a, b) => (b.connected ? 1 : 0) - (a.connected ? 1 : 0))
      if (!this.searchQuery.trim()) return sorted
      const q = this.searchQuery.toLowerCase()
      return sorted.filter(a =>
        a.name.toLowerCase().includes(q) ||
        (a.agent_code && a.agent_code.toLowerCase().includes(q)) ||
        (a.extension && a.extension.toLowerCase().includes(q))
      )
    },
    onlineCount () {
      return this.agents.filter(a => a.status === 'online').length
    },
    usedExtensions () {
      const map = {}
      for (const a of this.agents) {
        if (a.connected && a.extension) {
          map[a.extension] = { agent_id: a.agent_id, name: a.name }
        }
      }
      return map
    },
    filteredExtensions () {
      const q = this.extSearchQuery.toLowerCase().trim()
      if (!q) return this.pbxExtensions
      return this.pbxExtensions.filter(e =>
        e.extension.toLowerCase().includes(q) ||
        (e.display_name && e.display_name.toLowerCase().includes(q))
      )
    }
  },

  async mounted () {
    await this.load()
  },

  methods: {
    async load () {
      this.loading = true
      this.error = null
      try {
        const [agentsRes, extRes] = await Promise.all([
          IVRService.getAgentsExtensions(),
          IVRService.getExtensions()
        ])
        this.agents = agentsRes.agents || []
        this.sipHost = agentsRes.sip_host || null
        this.sipPort = agentsRes.sip_port || 5060
        this.pbxExtensions = extRes.extensions || []
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },

    openConnect (agent) {
      this.modalMode = 'connect'
      this.modalAgent = agent
      this.form = { extension: '', sip_password: '' }
      this.showPassword = false
      this.saveError = null
      this.extSearchQuery = ''
      this.showModal = true
    },

    openEdit (agent) {
      this.modalMode = 'edit'
      this.modalAgent = agent
      this.form = { extension: agent.extension || '', sip_password: '' }
      this.showPassword = false
      this.saveError = null
      this.extSearchQuery = ''
      this.showModal = true
    },

    closeModal () {
      if (this.saving) return
      this.showModal = false
    },

    async saveConnection () {
      this.saving = true
      this.saveError = null
      try {
        const payload = {
          agent_id: this.modalAgent.agent_id,
          extension: this.form.extension
        }
        if (this.form.sip_password) payload.sip_password = this.form.sip_password

        await IVRService.connectExtension(payload)

        // Update agent in local list without full reload
        const idx = this.agents.findIndex(a => a.agent_id === this.modalAgent.agent_id)
        if (idx !== -1) {
          this.agents[idx] = {
            ...this.agents[idx],
            extension: this.form.extension,
            connected: true
          }
        }
        this.showModal = false
      } catch (e) {
        this.saveError = e.message
      } finally {
        this.saving = false
      }
    },

    statusLabel (status) {
      const map = { online: 'Online', offline: 'Offline', unknown: 'Unknown' }
      return map[status] || status
    },

    relativeTime (dateStr) {
      if (!dateStr) return 'never'
      const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000)
      if (diff < 60) return 'just now'
      if (diff < 3600) return Math.floor(diff / 60) + ' min ago'
      if (diff < 86400) return Math.floor(diff / 3600) + ' hr ago'
      return Math.floor(diff / 86400) + ' d ago'
    },

    formatDate (str) {
      if (!str) return '—'
      return new Date(str).toLocaleString()
    }
  }
}
</script>

<style scoped>
.extensions-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}
.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}
.page-content {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
}

/* Page Header */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
}
.header-content h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px;
}
.subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.online-count {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #10B981;
  background: rgba(16,185,129,0.08);
  border: 1px solid rgba(16,185,129,0.2);
  padding: 6px 14px;
  border-radius: 8px;
}
.online-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10B981;
  animation: pulse 2s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Search */
.search-bar {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}
.search-wrap {
  position: relative;
  flex: 1;
  max-width: 440px;
}
.search-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 10px 36px 10px 40px;
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  font-size: 14px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.search-input:focus {
  outline: none;
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}
.search-clear {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  border-radius: 4px;
}
.search-clear:hover { color: var(--text-primary); }
.result-count { font-size: 13px; color: var(--text-secondary); }

/* Loading / Error / Empty */
.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}
.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-primary);
  border-top-color: #3B82F6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.error-state {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 12px;
  padding: 18px 20px;
  color: #DC2626;
  font-size: 14px;
}
.retry-btn {
  margin-top: 8px;
  padding: 6px 14px;
  border: 1px solid #FECACA;
  border-radius: 6px;
  background: #FEF2F2;
  color: #DC2626;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}
.retry-btn:hover { background: #FEE2E2; }

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 80px 20px;
  text-align: center;
}
.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  opacity: 0.5;
}
.empty-state h3 { font-size: 18px; font-weight: 600; color: var(--text-primary); margin: 0; }
.empty-state p { font-size: 14px; color: var(--text-secondary); margin: 0; }

/* Table */
.table-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  overflow: hidden;
}
.data-table { width: 100%; border-collapse: collapse; }
.data-table th {
  background: var(--bg-tertiary);
  padding: 12px 20px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid var(--border-primary);
}
.data-table td {
  padding: 14px 20px;
  border-bottom: 1px solid var(--border-primary);
  font-size: 14px;
  color: var(--text-primary);
}
.data-table tbody tr:last-child td { border-bottom: none; }
.data-table tbody tr:hover { background: var(--bg-tertiary); }
.row-connected { border-left: 3px solid #10B981; }
.row-connected:hover { background: rgba(16,185,129,0.03) !important; }

/* Agent cell */
.agent-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.agent-avatar {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.avatar-connected {
  background: rgba(16,185,129,0.1);
  border-color: rgba(16,185,129,0.25);
  color: #059669;
}
.agent-name { font-weight: 600; font-size: 14px; }
.agent-code-badge {
  font-size: 11px;
  font-family: monospace;
  color: var(--text-secondary);
  margin-top: 2px;
}

/* Extension */
.ext-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.ext-number {
  font-family: monospace;
  font-weight: 700;
  font-size: 15px;
  color: var(--text-primary);
  background: rgba(59,130,246,0.08);
  padding: 3px 10px;
  border-radius: 6px;
  border: 1px solid rgba(59,130,246,0.15);
}
.connected-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #059669;
  background: rgba(16,185,129,0.08);
  border: 1px solid rgba(16,185,129,0.2);
  padding: 2px 8px;
  border-radius: 20px;
}
.no-ext {
  font-size: 13px;
  color: var(--text-secondary);
  font-style: italic;
}

/* Type badge */
.type-badge {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 3px 8px;
  border-radius: 6px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  color: var(--text-secondary);
}

/* Status */
.status-cell { display: flex; align-items: center; gap: 8px; }
.status-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.dot-online { background: #10B981; box-shadow: 0 0 0 3px rgba(16,185,129,0.15); }
.dot-offline { background: #9CA3AF; }
.dot-unknown { background: #F59E0B; }
.status-label { font-size: 14px; font-weight: 500; }
.label-online { color: #059669; }
.label-offline { color: var(--text-secondary); }
.label-unknown { color: #D97706; }

.muted-cell { color: var(--text-secondary); font-size: 13px; }

/* Action */
.action-cell { width: 90px; }
.btn-action {
  padding: 6px 14px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}
.btn-connect { background: #3B82F6; color: #fff; }
.btn-connect:hover { background: #2563EB; transform: translateY(-1px); }
.btn-edit {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}
.btn-edit:hover { background: var(--border-primary); }

/* Buttons */
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  transition: all 0.2s;
}
.btn-secondary:hover:not(:disabled) { background: var(--bg-tertiary); }
.btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
.modal {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  width: 100%;
  max-width: 440px;
  overflow: hidden;
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 0;
}
.modal-header h2 {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}
.modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: var(--bg-tertiary);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: all 0.2s;
}
.modal-close:hover { background: var(--border-primary); color: var(--text-primary); }

.modal-body {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Agent display in modal */
.agent-display {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 10px;
}
.agent-display-avatar {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(59,130,246,0.1);
  border: 1px solid rgba(59,130,246,0.2);
  color: #3B82F6;
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.agent-display-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}
.agent-display-code {
  font-size: 12px;
  font-family: monospace;
  color: var(--text-secondary);
  margin-top: 2px;
}

.save-error {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #DC2626;
}

.form-group { display: flex; flex-direction: column; gap: 6px; }
.form-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 7px;
}
.label-optional {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  padding: 1px 7px;
  border-radius: 20px;
}
.form-input {
  padding: 12px 16px;
  border: 2px solid var(--border-primary);
  border-radius: 10px;
  font-size: 15px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  width: 100%;
}
.form-input:focus {
  outline: none;
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}

.password-wrap { position: relative; }
.password-wrap .form-input { padding-right: 44px; }
.show-pwd-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.2s;
}
.show-pwd-btn:hover { color: var(--text-primary); }

/* Connection summary */
.connect-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(16,185,129,0.08);
  border: 1px solid rgba(16,185,129,0.25);
  border-radius: 10px;
  font-size: 14px;
  color: #059669;
}
.connect-summary svg { flex-shrink: 0; }
.connect-summary strong { color: #065F46; font-weight: 700; }

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 24px;
  border-top: 1px solid var(--border-primary);
}
.btn-cancel {
  padding: 10px 20px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  transition: all 0.2s;
}
.btn-cancel:hover:not(:disabled) { background: var(--bg-tertiary); }
.btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-save {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: #10B981;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
}
.btn-save:hover:not(:disabled) { background: #059669; transform: translateY(-1px); }
.btn-save:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.btn-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* Extension picker */
.ext-picker {
  border: 2px solid var(--border-primary);
  border-radius: 10px;
  overflow: hidden;
  background: var(--bg-secondary);
  transition: border-color 0.2s, box-shadow 0.2s;
}
.ext-picker:focus-within {
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}
.ext-picker-search {
  position: relative;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-primary);
}
.ext-picker-search svg {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  pointer-events: none;
}
.ext-picker-input {
  width: 100%;
  padding: 9px 14px 9px 36px;
  border: none;
  background: transparent;
  font-size: 13px;
  color: var(--text-primary);
  box-sizing: border-box;
}
.ext-picker-input:focus { outline: none; }
.ext-picker-list {
  max-height: 210px;
  overflow-y: auto;
}
.ext-picker-empty {
  padding: 24px;
  text-align: center;
  font-size: 13px;
  color: var(--text-secondary);
}
.ext-picker-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 14px;
  border: none;
  border-bottom: 1px solid var(--border-primary);
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
  gap: 10px;
}
.ext-picker-item:last-child { border-bottom: none; }
.ext-picker-item:hover:not(:disabled) { background: var(--bg-tertiary); }
.ext-item-selected { background: rgba(59,130,246,0.06) !important; }
.ext-item-disabled { opacity: 0.45; cursor: not-allowed; }
.ext-item-left {
  display: flex;
  align-items: center;
  gap: 9px;
  flex: 1;
  min-width: 0;
}
.ext-item-number {
  font-family: monospace;
  font-weight: 700;
  font-size: 13px;
  color: var(--text-primary);
  background: rgba(59,130,246,0.08);
  border: 1px solid rgba(59,130,246,0.15);
  padding: 2px 8px;
  border-radius: 5px;
  flex-shrink: 0;
}
.ext-item-selected .ext-item-number {
  background: rgba(59,130,246,0.14);
  border-color: rgba(59,130,246,0.3);
}
.ext-item-name {
  font-size: 13px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ext-item-right {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
}
.ext-item-type {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  color: var(--text-secondary);
}
.ext-item-status {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.ext-status-registered { background: #10B981; }
.ext-status-unregistered { background: #9CA3AF; }
.ext-status-unknown { background: #F59E0B; }
.ext-item-inuse {
  font-size: 11px;
  color: #D97706;
  background: rgba(245,158,11,0.08);
  border: 1px solid rgba(245,158,11,0.2);
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
}
</style>
